import { orderBy } from 'lodash';
import type { CustomControlCode, ServiceCode } from '@common/enums';
import type { CustomControlData, IProcessActivity, Process } from '@common/interfaces';
import { ProcessActivityStatus } from '@common/enums';
import { nonNullable } from './nonNullableTypeUtils';

export function findProcessActivityByCustomControlCode<
    ProcessActivity extends { serviceActivity: { customControlCode: CustomControlCode } },
>(processActivities: ProcessActivity[], customControlCode: CustomControlCode): ProcessActivity | undefined {
    return processActivities.find(
        (processActivity) => processActivity.serviceActivity.customControlCode === customControlCode
    );
}

export function findProcessActivitiesByCustomControlCodes<
    ProcessActivity extends { serviceActivity: { customControlCode: CustomControlCode } },
>(processActivities: ProcessActivity[], customControlCodes: CustomControlCode[]): (ProcessActivity | undefined)[] {
    return customControlCodes.map((customControlCode) =>
        findProcessActivityByCustomControlCode(processActivities, customControlCode)
    );
}

export function getProcessActivityByCustomControlCode<
    ProcessActivity extends { serviceActivity: { customControlCode: CustomControlCode } },
>(processActivities: ProcessActivity[], customControlCode: CustomControlCode): ProcessActivity {
    return nonNullable(findProcessActivityByCustomControlCode(processActivities, customControlCode));
}

export function getCustomControlData<T>(
    process: Process,
    customControlCode: CustomControlCode,
    statuses: ProcessActivityStatus[] = [ProcessActivityStatus.complete, ProcessActivityStatus.draft]
): T | undefined {
    const processActivity = process.activities.find(
        (activity) =>
            statuses.includes(activity.status) && activity.serviceActivity.customControlCode === customControlCode
    );

    return processActivity?.customControlData as T | undefined;
}

export function orderProcessActivitiesOfCustomControlCodesByCreatedAt<
    T extends Pick<IProcessActivity<T>, 'created_at' | 'status' | 'serviceActivity'>,
>(
    processActivities: T[],
    customControlCode: CustomControlCode | CustomControlCode[],
    statuses?: ProcessActivityStatus[]
): T[] {
    const customControlCodes = Array.isArray(customControlCode) ? customControlCode : [customControlCode];

    return orderBy(
        processActivities.filter(
            (activity) =>
                (statuses ? statuses.includes(activity.status) : true) &&
                customControlCodes.includes(activity.serviceActivity.customControlCode)
        ),
        (activity) => activity.created_at,
        'desc'
    );
}

export function getLatestCustomControlData<T>(
    process: Process,
    customControlCode: CustomControlCode,
    statuses: ProcessActivityStatus[] = [ProcessActivityStatus.complete, ProcessActivityStatus.draft]
): T | undefined {
    const processActivities = orderProcessActivitiesOfCustomControlCodesByCreatedAt(
        process.activities,
        customControlCode,
        statuses
    );

    return processActivities?.[0]?.customControlData as T | undefined;
}

export function getLatestPerformedActivity<ProcessActivity extends { performedDatetime: Date | string }>(
    processActivities: ProcessActivity[]
): ProcessActivity {
    return orderBy(processActivities, (processActivity) => processActivity.performedDatetime, 'desc')[0];
}

export function getLastProcessActivityByCustomControlCodes(
    process: Process,
    customControlCodes: CustomControlCode[],
    statuses: ProcessActivityStatus[] = [ProcessActivityStatus.complete, ProcessActivityStatus.draft]
): IProcessActivity<CustomControlData> {
    const processActivities = orderProcessActivitiesOfCustomControlCodesByCreatedAt(
        process.activities,
        customControlCodes,
        statuses
    );

    return processActivities[0] ?? undefined;
}

export function getActivityPerformer(
    process: Process,
    customControlCode: CustomControlCode,
    statuses: ProcessActivityStatus[] = [ProcessActivityStatus.complete, ProcessActivityStatus.draft]
) {
    const processActivity = process.activities.find(
        (activity) =>
            statuses.includes(activity.status) && activity.serviceActivity.customControlCode === customControlCode
    );

    return {
        performerOrganization: processActivity?.performerOrganization,
        performerPractitioner: processActivity?.performerPractitioner,
    };
}

export function hasCompletedActivity(process: Process, customControlCode: CustomControlCode): boolean {
    return hasActivityWithStatus(process, customControlCode, [ProcessActivityStatus.complete]);
}

export function hasActivityWithStatus(
    process: Process,
    customControlCode: CustomControlCode,
    status: ProcessActivityStatus[]
): boolean {
    return process.activities.some(
        (activity) =>
            activity.serviceActivity.customControlCode === customControlCode && status.includes(activity.status)
    );
}

export function filterByServiceCode<T extends ServiceCode>(processes: Process[], serviceCode: T): Process<T>[] {
    return processes.filter((process) => process.service.code === serviceCode) as Process<T>[];
}

export function findSingleProcessByServiceCode<T extends ServiceCode>(
    processes: Process[],
    serviceCode: T
): Process<T> | undefined {
    const serviceProcesses = filterByServiceCode(processes, serviceCode);

    if (serviceProcesses.length > 1) {
        throw new Error(
            `Expected no or 1 process for service code ${serviceCode}, but found ${serviceProcesses.length}`
        );
    }

    return serviceProcesses[0];
}

export function findLatestProcessByServiceCode(processes: Process[], serviceCode: ServiceCode): Process | undefined {
    const serviceProcesses = filterByServiceCode(processes, serviceCode);

    if (!serviceProcesses.length) {
        return;
    }

    const orderedProcesses = orderBy(serviceProcesses, (process) => process.startedDatetime, 'desc');

    return orderedProcesses[0];
}
